import {
  type DomainGenerationSettings as OriginalDomainGenerationSettings,
  type DomainVariation,
  type DomainDescription, // Import the moved type
} from '~/types/domain';
import type { Tld } from '~/config/tld-config';
import { getAllTlds } from '~/config/tld-config';
// import type { DomainDescription } from '~/components/domain-dialog/domain-description';
import {
  getAuthToken,
  getEncryptionKeyFromToken,
} from '~/services/auth-service';
import { encryptPayload } from '~/utils/encryption';
import type { DomainSuggestion } from '~/types/domain';
import PQueue from 'p-queue';

// Define the result type for the new specific check function
export interface SpecificDomainCheckResult extends DomainVariation {
  name: string; // Ensure name is always present
  available?: boolean;
  error?: boolean;
  errorMessage?: string;
  aborted?: boolean; // Track aborted requests
  status?: 'unchecked' | 'checking' | 'available' | 'unavailable' | 'error';
}

// Extend the original settings type
export interface DomainGenerationSettings
  extends Omit<
    OriginalDomainGenerationSettings,
    'tldMode' | 'selectedTlds' | 'explicitTlds' // Omit fields replaced or not used here
  > {
  suggestTlds: boolean; // Use the new boolean flag
  // avoidExpensiveTlds is inherited if present in OriginalDomainGenerationSettings
  explicitTlds?: string[] | Tld[]; // Add explicit TLDs for explicit mode
  blacklist?: string[]; // Add blacklist parameter for domains to avoid
}

// Define interface for pricing results
export interface PricingInfo {
  results: {
    domain: string;
    available: boolean;
    price: number | null;
    isPremium: boolean;
  }[];
}

// Global availability cache that persists across searches - REMOVED
// const availabilityCache = new Map<string, boolean>();

// Removed ApiCallStats interface and apiCallStats object

// Map to store abort controllers for cancellable requests
const abortControllers = new Map<string, AbortController>();

// Export function to access the cache - REMOVED
// export function getAvailabilityFromCache(domain: string): boolean | undefined { ... }

// Export function to update the cache - REMOVED
// export function updateAvailabilityCache(domain: string, isAvailable: boolean): void { ... }

// Event listeners for progress updates
type ProgressListener = (progress: AvailabilityCheckProgress) => void;
const progressListeners: ProgressListener[] = [];

// Progress status object
export interface AvailabilityCheckProgress {
  active: number;
  queued: number;
  completed: number;
  total: number; // Represents total variations queued
  isComplete: boolean;
  percent: number;
  failed: number; // Number of failed variation checks
}

// Track auth state for availability checks
let isAuthError = false;
let authErrorTimestamp = 0;
const AUTH_RETRY_DELAY = 10000; // 10 seconds between auth retries
let isQueuePaused = false; // New flag to indicate if queue is paused due to auth errors
let tokenRefreshPromise: Promise<boolean> | null = null; // Track ongoing token refresh

// Notify listeners of progress updates - Defined here
function notifyProgressListeners() {
  // Use the new progress calculation based on variations
  const progress = getVariationCheckProgress();
  progressListeners.forEach((listener) => listener(progress));
}

// --- NEW Queue Processor for Specific Variations ---
async function processVariationQueue() {
  if (
    isProcessingVariationQueue ||
    variationQueue.length === 0 ||
    isQueuePaused
  ) {
    return;
  }
  isProcessingVariationQueue = true;

  const startNewVariationChecks = () => {
    if (isQueuePaused) return; // Don't start if paused

    while (
      variationQueue.length > 0 &&
      activeVariationCheckCount < MAX_CONCURRENT_VARIATION_CHECKS &&
      !isQueuePaused
    ) {
      const nextItem = variationQueue.shift();
      if (!nextItem) continue;

      const { variationName, resolve, reject, controller } = nextItem;
      activeVariationCheckCount++;
      notifyProgressListeners(); // Update progress: active increased, queued decreased

      // Perform the check for this single variation
      performSingleVariationCheck(variationName, controller)
        .then((result) => {
          resolve(result); // Resolve the promise for this specific variation
          totalVariationsProcessed++;
          // Clear failure state on success
          if (failedVariationChecks[variationName]) {
            delete failedVariationChecks[variationName];
          }
        })
        .catch((error) => {
          // Handle auth errors specifically to pause queue
          const isAuthenticationError =
            error instanceof Error &&
            (error.message.includes('session has expired') ||
              error.message.includes('Authentication required') ||
              error.message.includes('authentication'));

          if (isAuthenticationError && !isQueuePaused) {
            pauseQueueDueToAuthError(); // Pause the queue
            // We still need to reject the promise for the specific variation that failed
            reject(error);
            failedVariationChecks[variationName] = error.message || true; // Mark as failed
          } else {
            // Reject non-auth errors immediately
            reject(
              error instanceof Error
                ? error
                : new Error('Unknown error checking availability')
            );
            failedVariationChecks[variationName] =
              error instanceof Error ? error.message : true; // Mark as failed
          }
          totalVariationsProcessed++; // Count as processed even if failed
        })
        .finally(() => {
          activeVariationCheckCount--;
          notifyProgressListeners(); // Update progress: active decreased, completed increased

          // If not paused, try to start more checks if queue has items or finish processing
          if (!isQueuePaused) {
            if (variationQueue.length > 0) {
              startNewVariationChecks();
            } else if (activeVariationCheckCount === 0) {
              isProcessingVariationQueue = false;
              console.log('Variation check queue finished processing.');
              notifyProgressListeners(); // Final progress update
            }
          } else if (activeVariationCheckCount === 0) {
            // If paused and no active checks left, stop processing
            isProcessingVariationQueue = false;
            console.log('Variation check queue processing paused.');
            notifyProgressListeners();
          }
        });
    }
  };

  startNewVariationChecks();
}

// --- Helper function to perform the actual API call for a SINGLE variation ---
async function performSingleVariationCheck(
  variationName: string,
  controller: AbortController // Pass controller for cancellation
): Promise<SpecificDomainCheckResult> {
  const performApiRequest = async (): Promise<SpecificDomainCheckResult> => {
    const requestId = `${variationName}_${Date.now()}`; // Unique ID for this specific check
    // Store controller in the global map for potential external cancellation
    // Note: This overwrites if the same variation is queued again quickly,
    // cancellation might affect the latest request. Consider if per-job controller is enough.
    abortControllers.set(requestId, controller);

    try {
      // --- Auth and Rate Limiting Checks ---
      if (isQueuePaused) {
        throw new Error('Authentication error. System is refreshing token.');
      }
      const now = Date.now();
      if (isAuthError && now - authErrorTimestamp < AUTH_RETRY_DELAY) {
        throw new Error('Authentication error. Please refresh.');
      }
      // Basic rate limiting per request (consider if needed here or handled by queue timing)
      // if (now - lastCallTime < MIN_CALL_INTERVAL / MAX_CONCURRENT_VARIATION_CHECKS) { ... }
      // lastCallTime = now;

      // --- API Call ---
      // Removed apiCallStats increments

      const authToken = getAuthToken();
      if (!authToken) {
        isAuthError = true;
        authErrorTimestamp = now;
        if (!isQueuePaused) {
          pauseQueueDueToAuthError();
        }
        throw new Error('Authentication required.');
      }

      const queryParams = new URLSearchParams({
        domains: variationName,
      }).toString();
      const url = `/.netlify/functions/check-availability?${queryParams}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${authToken}` },
        signal: controller.signal, // Use the passed controller's signal
      });

      // --- Response Handling ---
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401 || response.status === 403) {
          isAuthError = true;
          authErrorTimestamp = now;
          if (!isQueuePaused) pauseQueueDueToAuthError();
          throw new Error('Session expired.');
        }
        // Handle other errors
        throw new Error(
          `API Error ${response.status}: ${errorData.error || 'Unknown'}`
        );
      }

      // --- Success Handling ---
      isAuthError = false; // Reset auth error state on success
      const apiResult = (await response.json()) as {
        results: DomainVariation[];
      };

      // Assuming the API returns an array even for a single domain
      const resultData = apiResult.results.find(
        (r) => r.name === variationName
      );

      if (!resultData) {
        throw new Error(`API response missing result for ${variationName}`);
      }

      // *** NEW LOGIC START ***
      // Check if the API reported an error during its lookup
      if (resultData.error) {
        return {
          name: variationName,
          available: undefined, // Availability is unknown due to error
          price: resultData.price, // Keep price if available
          error: true,
          errorMessage: 'Domain lookup failed on server.', // Generic message
          status: 'error',
        };
      }

      // *** NEW VALIDATION START ***
      // Check if 'available' property exists and is a boolean
      if (typeof resultData.available !== 'boolean') {
        console.warn(
          `[performSingleVariationCheck] API response for ${variationName} missing or invalid 'available' field. Treating as error. Response:`,
          resultData
        );
        return {
          name: variationName,
          available: undefined, // Availability is unknown
          price: resultData.price, // Keep price if available
          error: true,
          errorMessage: 'Invalid availability data received from server.',
          status: 'error',
        };
      }
      // *** NEW VALIDATION END ***

      // Existing logic for non-error cases (now assumes resultData.available is boolean)
      return {
        name: variationName,
        available: resultData.available, // Safe to access now
        price: resultData.price,
        // Add other fields if returned by API (isPremium etc.)
        error: false, // Explicitly false if no error reported by API
        status: resultData.available ? 'available' : 'unavailable',
      };
    } catch (error) {
      // --- Error Handling (including AbortError) ---
      if (error instanceof Error && error.name === 'AbortError') {
        console.log(`Request for variation ${variationName} was aborted.`);
        return {
          name: variationName,
          available: undefined,
          aborted: true,
          error: true,
          errorMessage: 'Aborted',
          status: 'error',
        };
      }

      // Check if it's an auth error needing retry
      if (
        error instanceof Error &&
        (error.message.includes('Session expired') ||
          error.message.includes('Authentication required'))
      ) {
        console.log(`Auth error for ${variationName}, attempting refresh...`);
        if (!isQueuePaused) pauseQueueDueToAuthError();

        // Try auth refresh via handleAuthFailure
        try {
          const { handleAuthFailure } = await import('~/services/auth-service');
          // Pass the *function* to retry, not the result of calling it
          const retryResult = await handleAuthFailure(performApiRequest);
          if (retryResult) {
            console.log(
              `Auth refresh & retry successful for ${variationName}.`
            );
            resumeQueueAfterTokenRefresh(); // Resume queue if paused
            return retryResult; // Return the result from the successful retry
          } else {
            console.log(`Auth refresh/retry failed for ${variationName}.`);
            // Fall through to return error state if retry failed
          }
        } catch (refreshError) {
          console.error(
            `Error during auth failure handling for ${variationName}:`,
            refreshError
          );
          // Fall through if handling itself failed
        }
      } // End auth error retry check

      // If we reach here, it's a non-auth error, failed retry, or AbortError
      console.error(`Failed check for ${variationName}:`, error);
      failedVariationChecks[variationName] =
        error instanceof Error ? error.message : true; // Mark as failed

      return {
        name: variationName,
        available: undefined,
        error: true,
        errorMessage:
          error instanceof Error ? error.message : 'Unknown check error',
        status: 'error',
      };
    } finally {
      // Remove the specific controller from the global map once done
      abortControllers.delete(requestId);
    }
  }; // End of performApiRequest definition

  return performApiRequest(); // Execute the request logic
}

/**
 * Pause queue processing due to authentication error
 * This will prevent new checks from starting until token is refreshed
 */
function pauseQueueDueToAuthError() {
  if (isQueuePaused) return; // Already paused

  console.log(
    'Pausing availability queue due to authentication error. Will resume after token refresh.'
  );
  isQueuePaused = true;
  isAuthError = true;
  authErrorTimestamp = Date.now();

  // Try to refresh the token and resume queue when done
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = refreshTokenAndResumeQueue();
  }
}

/**
 * Refresh the auth token and resume queue processing when successful
 */
async function refreshTokenAndResumeQueue(): Promise<boolean> {
  try {
    console.log('Attempting to refresh auth token before resuming queue...');

    // Import auth service functionality
    const { handleAuthFailure } = await import('~/services/auth-service');

    // Dummy function that just returns true if token refresh succeeded
    const checkAuthSuccess = async () => true;

    // Try to refresh token
    const success = await handleAuthFailure(checkAuthSuccess);

    if (success) {
      resumeQueueAfterTokenRefresh();
      return true;
    } else {
      console.log('Token refresh failed, queue remains paused');
      return false;
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
    return false;
  } finally {
    // Clear the promise reference
    tokenRefreshPromise = null;
  }
}

/**
 * Resume queue processing after successful token refresh
 */
export function resumeQueueAfterTokenRefresh() {
  if (!isQueuePaused) return; // Not paused, nothing to do

  console.log(
    'Auth token refreshed, resuming variation check queue processing'
  ); // Updated log message
  isQueuePaused = false;
  isAuthError = false; // Reset auth error state

  // Restart VARIATION queue processing if there are items
  if (variationQueue.length > 0 && !isProcessingVariationQueue) {
    console.log('Restarting variation queue processing after auth refresh.');
    processVariationQueue();
  }
  // Optionally restart the old queue if still used
  // if (availabilityQueue.length > 0 && !isProcessingQueue) {
  //   processAvailabilityQueue();
  // }
}

// Add tracking to getDomainDescription function
export async function getDomainDescription(
  domainName: string,
  language: string,
  userQuery: string
): Promise<DomainDescription> {
  // Removed apiCallStats increment

  // Get auth token from auth service
  const authToken = getAuthToken();

  // Throw error if no auth token is available
  if (!authToken) {
    throw new Error(
      'Authentication required. Please wait for authentication to complete.'
    );
  }

  try {
    const response = await fetch('/.netlify/functions/get-domain-description', {
      method: 'POST',
      body: JSON.stringify({
        domain: domainName,
        language,
        userQuery,
      }),
      headers: {
        'Content-Type': 'application/json',
        // Always include the authentication header
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Domain description failed: ${response.status} - ${errorData.error || 'Unknown error'}`
      );
    }

    return response.json();
  } catch (error) {
    console.error(`Failed to get domain description for ${domainName}:`, error);
    throw error;
  }
}

// --- NEW Function to Clear the Variation Queue ---
export function clearVariationQueue(): void {
  console.log(
    `Clearing variation queue. ${variationQueue.length} items pending.`
  );
  // Abort pending requests before clearing
  cancelAllPendingRequests(); // cancelAllPendingRequests now uses the global map

  const resolvingItems = [...variationQueue];
  variationQueue = []; // Clear the queue

  // Reset counters for the new queue
  totalVariationsQueued = 0;
  totalVariationsProcessed = 0;
  activeVariationCheckCount = 0; // Reset active count
  failedVariationChecks = {}; // Clear failed state
  successfullyCheckedVariations = {}; // Clear successful checks cache as well
  pendingVariationChecks.clear(); // Clear pending checks set
  isProcessingVariationQueue = false; // Ensure processor stops

  // Notify listeners
  notifyProgressListeners();

  // Resolve pending promises with an 'aborted' state
  resolvingItems.forEach((item) => {
    item.resolve({
      name: item.variationName,
      available: undefined,
      error: true, // Mark as error because it didn't complete
      aborted: true,
      errorMessage: 'Queue cleared',
    });
  });

  console.log('Variation check queue cleared.');
}

// --- Pricing API Functions ---

export async function getGoDaddyPricing(
  domainNames: string[]
): Promise<PricingInfo> {
  // Removed apiCallStats increment

  // Get auth token from auth service
  const authToken = getAuthToken();

  // Throw error if no auth token is available
  if (!authToken) {
    throw new Error(
      'Authentication required. Please wait for authentication to complete.'
    );
  }

  const response = await fetch('/.netlify/functions/godaddy-pricing', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Always include the authentication header
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ domainNames }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      `GoDaddy Pricing API error: ${response.status} - ${errorData.error || 'Unknown error'}`
    );
  }

  return response.json();
}

export async function getNamecheapPricing(
  domainNames: string[]
): Promise<PricingInfo> {
  // Removed apiCallStats increment

  // Get auth token from auth service
  const authToken = getAuthToken();

  // Throw error if no auth token is available
  if (!authToken) {
    throw new Error(
      'Authentication required. Please wait for authentication to complete.'
    );
  }

  const response = await fetch('/.netlify/functions/namecheap-pricing', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Always include the authentication header
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ domainNames }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      `Namecheap Pricing API error: ${response.status} - ${errorData.error || 'Unknown error'}`
    );
  }

  return response.json();
}

// Subscribe to progress updates
export function subscribeToProgress(listener: ProgressListener): () => void {
  progressListeners.push(listener);

  // Return unsubscribe function
  return () => {
    const index = progressListeners.indexOf(listener);
    if (index !== -1) {
      progressListeners.splice(index, 1);
    }
  };
}

// Get the current progress state - UPDATED FOR VARIATIONS
export function getVariationCheckProgress(): AvailabilityCheckProgress {
  const total = totalVariationsQueued; // Use variation counts
  const completed = totalVariationsProcessed; // Use variation counts
  const active = activeVariationCheckCount; // Use variation counts
  // Queued is harder to define precisely as batches are added.
  // We can estimate based on total - completed - active, but it might be negative briefly.
  // Or report the current variationQueue.length
  const queued = variationQueue.length; // Current items waiting in the immediate queue
  // const queued = Math.max(0, total - completed - active); // Alternative estimated queued

  const failed = Object.keys(failedVariationChecks).length; // Count failed variations

  // Completion logic might need refinement depending on how `totalVariationsQueued` is managed
  // (e.g., does it only increment when a batch starts, or represent the total ever intended?)
  // Assuming totalQueued reflects the total number of variations *intended* to be checked
  const isComplete = completed === total && active === 0 && queued === 0;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return {
    active,
    queued, // Represents items currently in the JS queue array
    completed,
    total, // Represents total ever added to the queue system
    isComplete,
    percent,
    failed,
  };
}

/**
 * Cancel all pending domain availability checks
 * Call this when unmounting components or when navigating away
 */
export function cancelAllPendingRequests(): void {
  console.log(
    `Cancelling ${abortControllers.size} pending variation availability requests` // Updated log
  );
  // Abort controllers associated with variations in the current queue
  variationQueue.forEach((job) => {
    try {
      job.controller.abort();
      console.log(`Aborted queued request for: ${job.variationName}`);
    } catch (error) {
      console.error(
        `Error aborting queued request ${job.variationName}:`,
        error
      );
    }
  });

  // Abort controllers stored in the global map (covers active requests)
  abortControllers.forEach((controller, id) => {
    try {
      // Check if already aborted (might happen if controller is shared or aborted above)
      if (!controller.signal.aborted) {
        controller.abort();
        console.log(`Aborted active/mapped request: ${id}`);
      }
    } catch (error) {
      console.error(`Error aborting mapped request ${id}:`, error);
    }
  });
  abortControllers.clear(); // Clear the map after aborting
}

// --- NEW Public Function to Check Specific Variations ---
/**
 * Checks the availability of a list of specific domain variations.
 * Manages a queue and concurrency limits internally.
 * Returns a Promise that resolves with an array of results corresponding
 * to the input variations, maintaining the original order.
 */
export async function checkSpecificVariations(
  variationNames: string[]
): Promise<SpecificDomainCheckResult[]> {
  console.log(
    `Queuing ${variationNames.length} specific variations for check.`
  );

  // Filter out variations that:
  // 1. Are already known failures (unless it was an abort)
  // 2. Have been successfully checked already
  // 3. Are currently being processed in another request
  const variationsToQueue = variationNames.filter((name) => {
    // Skip if it's a known failure that wasn't due to abort
    if (
      failedVariationChecks[name] &&
      failedVariationChecks[name] !== 'Aborted'
    ) {
      return false;
    }

    // Skip if it's already been successfully checked
    if (successfullyCheckedVariations[name]) {
      console.log(`Skipping ${name} - already successfully checked`);
      return false;
    }

    // Skip if it's currently being processed
    if (pendingVariationChecks.has(name)) {
      console.log(`Skipping ${name} - already in progress`);
      return false;
    }

    // Include all other variations
    return true;
  });

  // Clear abort failures so they can be retried
  variationsToQueue.forEach((name) => {
    if (failedVariationChecks[name] === 'Aborted') {
      delete failedVariationChecks[name];
    }
    // Mark this variation as pending
    pendingVariationChecks.add(name);
  });

  if (variationsToQueue.length < variationNames.length) {
    console.log(
      `Skipped ${variationNames.length - variationsToQueue.length} variations due to previous checks, pending checks, or non-abort errors.`
    );
  }

  // Create promises for each variation to be checked
  const promises = variationsToQueue.map((variationName) => {
    return new Promise<SpecificDomainCheckResult>((resolve, reject) => {
      const controller = new AbortController(); // Create a controller for this job
      // Add job to the variation queue
      variationQueue.push({ variationName, resolve, reject, controller });
      totalVariationsQueued++; // Increment total count
    });
  });

  // Notify listeners about the newly queued items
  notifyProgressListeners();

  // Start processing the variation queue if not already running
  if (!isProcessingVariationQueue) {
    processVariationQueue();
  }

  // Wait for all the promises created for this batch to settle
  const resultsFromQueue = await Promise.allSettled(promises);

  // Map settled results back to the original input array structure
  const finalResults: SpecificDomainCheckResult[] = variationNames.map(
    (originalName) => {
      // First check: if we already have this in our cache of successful checks, use that
      if (successfullyCheckedVariations[originalName]) {
        // Remove from pending set if it was there
        pendingVariationChecks.delete(originalName);
        return successfullyCheckedVariations[originalName];
      }

      // Find the index of this name in the *queued* variations
      const queuedIndex = variationsToQueue.indexOf(originalName);

      if (queuedIndex === -1) {
        // This variation was skipped due to a previous hard failure
        // Remove from pending set if it was there
        pendingVariationChecks.delete(originalName);

        // Create result with properly typed status
        const result: SpecificDomainCheckResult = {
          name: originalName,
          available: undefined,
          status: 'error',
          error: true,
          errorMessage:
            typeof failedVariationChecks[originalName] === 'string'
              ? (failedVariationChecks[originalName] as string)
              : 'Previously failed', // Provide the stored error message
        };

        return result;
      }

      // Get the settled result for this variation
      const settledResult = resultsFromQueue[queuedIndex];

      if (settledResult.status === 'fulfilled') {
        // Determine the status based on the value properties
        let status:
          | 'unchecked'
          | 'checking'
          | 'available'
          | 'unavailable'
          | 'error';

        if (settledResult.value.available === true) {
          status = 'available';
        } else if (settledResult.value.available === false) {
          status = 'unavailable';
        } else if (settledResult.value.error) {
          status = 'error';
        } else {
          status = 'unchecked';
        }

        // Create a new result with the status field
        const valueWithStatus: SpecificDomainCheckResult = {
          ...settledResult.value,
          status,
        };

        // Store successful results in our cache
        successfullyCheckedVariations[originalName] = valueWithStatus;
        // Remove from pending set
        pendingVariationChecks.delete(originalName);
        return valueWithStatus;
      } else {
        // Handle rejected promise (error during check)
        const reason = settledResult.reason;
        console.error(
          `Check for ${originalName} failed in Promise.allSettled:`,
          reason
        );
        // Remove from pending set
        pendingVariationChecks.delete(originalName);

        // Create result with properly typed status
        const result: SpecificDomainCheckResult = {
          name: originalName,
          available: undefined,
          status: 'error',
          error: true,
          errorMessage:
            reason instanceof Error
              ? reason.message
              : 'Failed during batch processing',
          aborted: reason instanceof Error && reason.name === 'AbortError', // Check if it was an abort
        };

        return result;
      }
    }
  );

  return finalResults;
}

// Retry failed domain checks - TODO: Adapt for variation-based failures
// (Keep the function signature but comment out implementation for now)
export function retryFailedChecks(): Promise<void> {
  const failedVariationNames = Object.keys(failedVariationChecks);
  if (failedVariationNames.length === 0) {
    console.log('No failed variations to retry.');
    return Promise.resolve();
  }

  console.log(
    `Retrying ${failedVariationNames.length} failed variation checks...`
  );

  // Clear the failed state before retrying
  const variationsToRetry = [...failedVariationNames];
  failedVariationChecks = {}; // Reset failed checks

  // Re-queue these variations for checking using the new function
  return checkSpecificVariations(variationsToRetry)
    .then(() => {
      console.log(`Retried ${variationsToRetry.length} variations.`);
      notifyProgressListeners(); // Update progress after retry attempt
    })
    .catch((error) => {
      console.error('Error occurred during the retry batch:', error);
      notifyProgressListeners(); // Still update progress
      // Decide how to handle errors during retry itself
    });
}

// --- NEW Queue Management for Specific Variation Checks ---
interface VariationCheckJob {
  variationName: string;
  resolve: (result: SpecificDomainCheckResult) => void;
  reject: (reason: Error) => void;
  controller: AbortController; // Abort controller per variation
}

// Restore declarations for the new queue system
let variationQueue: VariationCheckJob[] = [];
let isProcessingVariationQueue = false;
const MAX_CONCURRENT_VARIATION_CHECKS = 5; // Keep concurrency limit
let activeVariationCheckCount = 0;
let totalVariationsQueued = 0;
let totalVariationsProcessed = 0;
let failedVariationChecks: Record<string, string | true> = {}; // Track failed variations
let successfullyCheckedVariations: Record<string, SpecificDomainCheckResult> =
  {}; // Track successfully checked variations
// Track variations that are currently being processed (to prevent duplicate requests)
const pendingVariationChecks: Set<string> = new Set();

// Re-add the generateDomains function
export async function generateDomains(
  settings: DomainGenerationSettings
): Promise<{ suggestions: DomainSuggestion[]; generatedTlds?: string[] }> {
  // Removed apiCallStats increment
  clearVariationQueue();

  let tldsToUseLocally: string[] = [];

  try {
    const now = Date.now();
    // Don't proceed if queue is paused due to auth error
    if (isQueuePaused) {
      throw new Error(
        'Authentication error. System is refreshing authentication token. Please wait...'
      );
    }

    // Check if we're in an auth error state and need to wait before retrying
    if (isAuthError && now - authErrorTimestamp < AUTH_RETRY_DELAY) {
      throw new Error(
        'Authentication error. Please refresh the page to re-authenticate.'
      );
    }

    const authToken = getAuthToken();
    if (!authToken) {
      isAuthError = true;
      authErrorTimestamp = now;
      if (!isQueuePaused) {
        pauseQueueDueToAuthError();
      }
      throw new Error(
        'Authentication required. Please refresh the page to authenticate your session.'
      );
    }

    // The actual API request operation as a separate function
    const performApiRequest = async (): Promise<{
      suggestions: DomainSuggestion[];
      generatedTlds?: string[];
    }> => {
      const {
        query,
        mode,
        maxLength,
        creativityLevel,
        domainLanguage,
        suggestTlds,
        avoidExpensiveTlds,
        blacklist,
      } = settings;

      const requestBody = {
        query: query,
        mode,
        maxLength,
        creativityLevel,
        language: domainLanguage,
        suggestTlds,
        avoidExpensiveTlds,
        blacklist,
      };

      const currentToken = getAuthToken();
      if (!currentToken) {
        throw new Error(
          'Authentication token not available during API request'
        );
      }

      const encryptionKey = getEncryptionKeyFromToken();
      let finalRequestBody: any = requestBody;

      if (encryptionKey) {
        try {
          finalRequestBody = await encryptPayload(requestBody, encryptionKey);
        } catch (error) {
          console.warn('Encryption failed, sending unencrypted payload', error);
        }
      } else {
        console.warn('No encryption key found, sending unencrypted payload');
      }

      const response = await fetch('/.netlify/functions/generate-domains', {
        method: 'POST',
        body: JSON.stringify(finalRequestBody),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Session expired. Please refresh.');
        }
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please wait.');
        }
        if (response.status === 504 || response.status === 502) {
          throw new Error('Generation service unavailable. Try later.');
        }
        const errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(
            `Generation failed: ${errorJson.error || response.statusText}`
          );
        } catch {
          throw new Error(`Generation failed (${response.status}).`);
        }
      }

      // Define DomainApiResponse locally if it was removed
      interface DomainApiResponse {
        baseNames: string[];
        suggestedTlds?: string[];
      }
      const { baseNames, suggestedTlds } =
        (await response.json()) as DomainApiResponse;

      if (!baseNames || baseNames.length === 0) {
        throw new Error('No domain suggestions generated.');
      }

      // Determine TLDs to use locally based on settings and API response
      if (settings.suggestTlds && suggestedTlds && suggestedTlds.length > 0) {
        tldsToUseLocally = suggestedTlds;
      } else if (settings.explicitTlds && settings.explicitTlds.length > 0) {
        tldsToUseLocally = getAllTlds(settings.explicitTlds);
      } else {
        // Default fallback: Use all TLDs if not suggesting, no explicit TLDs, or API didn't suggest any
        tldsToUseLocally = getAllTlds();
      }

      const domainSuggestions = baseNames.map((baseName) => ({
        baseName: baseName,
        checkingAvailability: true, // Mark as checking initially
        variations: tldsToUseLocally.map((tld: string) => ({
          name: `${baseName}${tld}`,
          available: undefined,
          price: undefined,
          error: false,
          status: 'unchecked' as const,
        })),
      }));

      return {
        suggestions: domainSuggestions,
        generatedTlds: tldsToUseLocally,
      };
    };

    // First attempt
    try {
      return await performApiRequest();
    } catch (error) {
      // Check if this is an authentication error
      if (
        error instanceof Error &&
        (error.message.includes('Session expired') ||
          error.message.includes('Authentication required'))
      ) {
        console.log('Auth error during generateDomains, attempting refresh...');
        isAuthError = true;
        authErrorTimestamp = now;
        if (!isQueuePaused) pauseQueueDueToAuthError();

        const { handleAuthFailure } = await import('~/services/auth-service');
        if (handleAuthFailure) {
          const result = await handleAuthFailure(performApiRequest);
          if (result) {
            resumeQueueAfterTokenRefresh();
            return result;
          }
        }
      }
      // If not auth error or retry failed, re-throw
      throw error;
    }
  } catch (error) {
    console.error('Domain generation failed:', error);
    throw error; // Re-throw after logging
  }
}

/**
 * Clears the cache of successfully checked domains.
 * Can be called periodically to prevent memory leaks or when results might be stale.
 */
export function clearSuccessfulVariationsCache(): void {
  successfullyCheckedVariations = {};
  pendingVariationChecks.clear(); // Also clear pending checks
  console.log('Successfully checked variations cache cleared');
}

/**
 * Checks the availability of multiple domain names, each with a list of TLDs.
 * For each domain, checks TLDs one at a time (stopping at first available),
 * and processes up to 5 domains in parallel.
 * @param domainTldMap - An array of { baseName, tlds } objects
 * @returns Promise resolving to an array of { baseName, availableTld, result } objects
 */
export async function checkDomainsWithTldQueue(
  domainTldMap: { baseName: string; tlds: string[] }[]
): Promise<
  {
    baseName: string;
    availableTld: string | null;
    result: SpecificDomainCheckResult | null;
    allResults: SpecificDomainCheckResult[];
  }[]
> {
  console.log(
    `[checkDomainsWithTldQueue] Checking ${domainTldMap.length} domains with TLDs:`
  );
  domainTldMap.forEach(({ baseName, tlds }) => {
    console.log(`  - ${baseName}: ${tlds.join(', ')}`);
  });

  const mainQueue = new PQueue({ concurrency: 5 });

  // For each domain, create a sub-queue to check TLDs in order
  const domainTasks = domainTldMap.map(({ baseName, tlds }) =>
    mainQueue.add(
      async (): Promise<{
        baseName: string;
        availableTld: string | null;
        result: SpecificDomainCheckResult | null;
        allResults: SpecificDomainCheckResult[];
      }> => {
        console.log(
          `[Domain Queue] Starting checks for domain: ${baseName} with TLDs: ${tlds.join(', ')}`
        );

        const tldQueue = new PQueue({ concurrency: 1 });
        let found: SpecificDomainCheckResult | null = null;
        let foundTld: string | null = null;
        const allResults: SpecificDomainCheckResult[] = [];

        // If no TLDs provided, don't check anything
        if (!tlds.length) {
          console.log(`[Domain Queue] No TLDs to check for ${baseName}`);
          return { baseName, availableTld: null, result: null, allResults: [] };
        }

        // Create a flag to track if we've found an available domain
        let availableDomainFound = false;

        for (const tld of tlds) {
          // Skip remaining TLDs if we already found an available one
          if (availableDomainFound) {
            console.log(
              `[TLD Queue] Skipping ${tld} for ${baseName} because already found available: ${foundTld}`
            );
            continue;
          }

          // Only add to the queue if we haven't found an available domain yet
          await tldQueue.add(async () => {
            // Double-check the flag before performing the check
            if (availableDomainFound) return;

            const variation = `${baseName}${tld}`;
            console.log(`[TLD Queue] Checking variation: ${variation}`);
            try {
              const [result] = await checkSpecificVariations([variation]);
              console.log(
                `[TLD Queue] Result for ${variation}: ${result.available ? 'AVAILABLE' : 'NOT AVAILABLE'}`
              );

              // Store all results for UI updates
              allResults.push(result);

              // If available, note it as found and set the flag to stop further checks
              if (result.available) {
                found = result;
                foundTld = tld;
                availableDomainFound = true; // Set flag to prevent more checks
                console.log(
                  `[TLD Queue] Found available domain: ${variation}, stopping further checks for ${baseName}`
                );

                // Clear the queue to cancel any pending tasks for this domain
                tldQueue.clear();
              }
            } catch (error) {
              console.error(`[TLD Queue] Error checking ${variation}:`, error);
              // Add error result for this variation
              allResults.push({
                name: variation,
                available: false,
                error: true,
                errorMessage:
                  error instanceof Error ? error.message : 'Unknown error',
                status: 'error',
              });
            }
          });

          // If we found an available domain after processing this TLD, break out of the loop
          if (availableDomainFound) {
            console.log(
              `[TLD Queue] Breaking out of TLD loop for ${baseName} after finding available domain`
            );
            break;
          }
        }

        await tldQueue.onIdle();
        console.log(
          `[Domain Queue] Completed checks for domain: ${baseName}, found available: ${foundTld || 'NONE'}, checked ${allResults.length} variations`
        );

        return {
          baseName,
          availableTld: foundTld,
          result: found,
          allResults,
        };
      }
    )
  );

  // Fix: Explicitly cast the resolved promises to the expected type
  const results = (await Promise.all(domainTasks)) as {
    baseName: string;
    availableTld: string | null;
    result: SpecificDomainCheckResult | null;
    allResults: SpecificDomainCheckResult[];
  }[];

  console.log(
    `[checkDomainsWithTldQueue] All domain checks completed. Results summary:`
  );
  results.forEach(({ baseName, availableTld, allResults }) => {
    console.log(
      `  - ${baseName}: ${availableTld || 'No available TLD'} (checked ${allResults.length} variations)`
    );
  });

  return results;
}

// --- NEW Progress Update Types ---
export type AvailabilityProgressUpdate =
  | {
      type: 'variation_result';
      result: SpecificDomainCheckResult; // The result for a single checked variation
    }
  | {
      type: 'domain_complete';
      baseName: string;
      status: 'available' | 'unavailable' | 'error'; // Final status for the domain
    }
  | {
      type: 'progress';
      completed: number; // Number of domains completed
      total: number; // Total number of domains being checked
      percent: number;
    }
  | {
      type: 'all_complete';
      totalChecked: number; // Total variations that were actually checked
    }
  | { type: 'error'; message: string };

export type AvailabilityProgressCallback = (
  update: AvailabilityProgressUpdate
) => void;
// --- End NEW Progress Update Types ---

// --- NEW Orchestration Function ---

// Store the current orchestration state (needed for cancellation)
let currentOrchestrationAbortController: AbortController | null = null;

/**
 * Orchestrates the entire domain availability check process.
 * Manages concurrency and sequential TLD checks per domain.
 *
 * @param initialDomains - The initial list of domain suggestions.
 * @param tldPriority - An ordered array of TLD strings (e.g., ['.com', '.io']) to check.
 * @param progressCallback - Function called with updates on the checking process.
 * @returns A function to cancel the ongoing orchestration.
 */
export function orchestrateAvailabilityChecks(
  initialDomains: DomainSuggestion[],
  tldPriority: string[],
  progressCallback: AvailabilityProgressCallback
): () => void {
  // Cancel any previous orchestration
  if (currentOrchestrationAbortController) {
    console.log('[Orchestrator] Cancelling previous check orchestration.');
    currentOrchestrationAbortController.abort();
  }

  // Create a new AbortController for this orchestration
  const abortController = new AbortController();
  currentOrchestrationAbortController = abortController;
  const signal = abortController.signal;

  // Reset necessary states (optional, depending on desired behavior on new search)
  clearSuccessfulVariationsCache();
  // Maybe reset failedVariationChecks?
  // Maybe reset progress counters?

  console.log(
    `[Orchestrator] Starting checks for ${initialDomains.length} domains with TLD priority: ${tldPriority.join(', ')}`
  );

  const totalDomains = initialDomains.length;
  let completedDomains = 0;
  let totalVariationsActuallyChecked = 0;

  const mainQueue = new PQueue({ concurrency: 5 });

  // Function to process a single domain
  const processDomain = async (domain: DomainSuggestion) => {
    if (signal.aborted) return; // Check for cancellation

    console.log(`[Orchestrator] Processing domain: ${domain.baseName}`);
    let domainStatus: 'available' | 'unavailable' | 'error' = 'unavailable'; // Default to unavailable
    let availableFound = false;

    for (const tld of tldPriority) {
      if (signal.aborted) break; // Check for cancellation
      if (availableFound) break; // Stop if available TLD already found for this domain

      const variationName = `${domain.baseName}${tld}`; // Ensure TLD starts with .

      try {
        // Check one variation at a time
        const [result] = await checkSpecificVariations([variationName]);
        totalVariationsActuallyChecked++;

        // Report individual variation result immediately
        if (!signal.aborted) {
          progressCallback({ type: 'variation_result', result });
        }

        if (signal.aborted) break;

        if (result.available) {
          console.log(
            `[Orchestrator] Available variation found for ${domain.baseName}: ${variationName}`
          );
          availableFound = true;
          domainStatus = 'available';
          // No need to check further TLDs for this domain
          break; // Exit TLD loop immediately
        } else if (result.error && !result.aborted) {
          // If a non-abort error occurred, mark domain as error and stop checking TLDs for it
          console.log(
            `[Orchestrator] Error checking ${variationName}. Marking ${domain.baseName} as error.`
          );
          domainStatus = 'error';
          availableFound = true; // Use flag to stop further TLD checks
          break; // Exit TLD loop immediately
        }
      } catch (error) {
        totalVariationsActuallyChecked++; // Count even if checkSpecificVariations itself throws
        console.error(
          `[Orchestrator] Critical error checking variation ${variationName}:`,
          error
        );
        if (!signal.aborted) {
          progressCallback({
            type: 'error',
            message: `Error checking ${variationName}`,
          });
        }
        // Mark domain as error and stop checking TLDs for it
        domainStatus = 'error';
        availableFound = true; // Stop further checks
        break; // Exit TLD loop immediately
      }
    } // End TLD loop

    // Report domain completion status only if not aborted
    if (!signal.aborted) {
      completedDomains++;
      console.log(
        `[Orchestrator] Domain ${domain.baseName} completed with status: ${domainStatus}`
      );
      progressCallback({
        type: 'domain_complete',
        baseName: domain.baseName,
        status: domainStatus,
      });
      progressCallback({
        type: 'progress',
        completed: completedDomains,
        total: totalDomains,
        percent: Math.round((completedDomains / totalDomains) * 100),
      });
    }
  };

  // Add all domains to the main queue
  initialDomains.forEach((domain) => {
    // Ensure domain has variations before queueing
    if (domain.variations && domain.variations.length > 0) {
      mainQueue
        .add(() => processDomain(domain))
        .catch((error) => {
          // Handle errors from processDomain if any bubble up (should be caught inside ideally)
          console.error(
            `[Orchestrator] Error processing domain ${domain.baseName} in queue:`,
            error
          );
          if (!signal.aborted) {
            completedDomains++; // Count as completed even if errored
            progressCallback({
              type: 'domain_complete',
              baseName: domain.baseName,
              status: 'error',
            });
            progressCallback({
              type: 'progress',
              completed: completedDomains,
              total: totalDomains,
              percent: Math.round((completedDomains / totalDomains) * 100),
            });
          }
        });
    } else {
      // If domain has no variations, immediately mark as complete (unavailable)
      console.warn(
        `[Orchestrator] Domain ${domain.baseName} has no variations, marking as unavailable.`
      );
      if (!signal.aborted) {
        completedDomains++;
        progressCallback({
          type: 'domain_complete',
          baseName: domain.baseName,
          status: 'unavailable',
        });
        progressCallback({
          type: 'progress',
          completed: completedDomains,
          total: totalDomains,
          percent: Math.round((completedDomains / totalDomains) * 100),
        });
      }
    }
  });

  // When the main queue is idle, signal completion
  mainQueue.onIdle().then(() => {
    if (!signal.aborted) {
      console.log('[Orchestrator] All domain checks complete.');
      progressCallback({
        type: 'all_complete',
        totalChecked: totalVariationsActuallyChecked,
      });
      currentOrchestrationAbortController = null; // Clear controller ref
    } else {
      console.log('[Orchestrator] Orchestration aborted before completion.');
    }
  });

  // Return the cancellation function
  return () => {
    console.log('[Orchestrator] Cancellation requested.');
    if (!abortController.signal.aborted) {
      abortController.abort();
    }
    mainQueue.clear(); // Clear pending tasks in the main queue
    // Also cancel any *active* variation checks initiated by checkSpecificVariations
    cancelAllPendingRequests();
    currentOrchestrationAbortController = null;
  };
}
// --- End NEW Orchestration Function ---
